import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Avatar,
  Paper,
  CircularProgress,
  Fade,
  Chip
} from '@mui/material';
import axios from 'src/utils/axios';
import { getUser } from 'src/utils/Common';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Modern stil tanımları
const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      maxHeight: '85vh',
    },
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: '#ffffff',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  dialogContent: {
    padding: '24px',
    backgroundColor: '#f8fafc',
    minHeight: '300px',
  },
  commentCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '12px',
  },
  avatar: {
    width: 40,
    height: 40,
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    fontWeight: 600,
  },
  commentMeta: {
    flex: 1,
  },
  userName: {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '0.9rem',
  },
  dateTime: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#64748b',
    fontSize: '0.75rem',
    marginTop: '2px',
  },
  commentText: {
    color: '#475569',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    paddingLeft: '52px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#cbd5e1',
    marginBottom: '16px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    gap: '16px',
  },
  dialogActions: {
    padding: '20px 24px',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    flexDirection: 'column',
    gap: '12px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#f8fafc',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#f1f5f9',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
      },
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    width: '100%',
  },
  sendButton: {
    flex: 1,
    borderRadius: '12px',
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
    },
  },
  closeActionButton: {
    borderRadius: '12px',
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  projectChip: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    fontWeight: 500,
  },
};

const CommentPopup = ({ open, setOpen, projectId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const user = getUser();

  const fetchData = () => {
    setLoading(true);
    axios
      .get('/GetHavcProjectComment', { params: { projectId } })
      .then(response => {
        setComments(response.data || []);
      })
      .catch(error => {
        console.error('Veri alınırken hata:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, projectId]);

  const handleSend = () => {
    if (newComment.trim() === '') {
      return;
    }

    setSending(true);

    axios
      .post('/SetHavcProjectComment', {
        projectId: projectId,
        yorum: newComment.trim(),
      }, {
        headers: { 'Content-Type': 'application/json' }
      })
      .then(response => {
        setComments([...comments, { 
          Yorum: newComment.trim(), 
          kaydeden: user.FirstName + ' ' + user.LastName, 
          YorumTarihi: new Date() 
        }]);
        setNewComment('');
      })
      .catch(error => {
        console.error('Veri gönderilirken hata:', error);
      })
      .finally(() => {
        setSending(false);
      });
  };

  const handleClose = () => {
    setComments([]);
    setNewComment('');
    setOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      sx={styles.dialog}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleContent}>
          <Box sx={styles.titleIcon}>
            <ChatBubbleOutlineIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '2px' }}>
              Proje Yorumları
            </Typography>
            <Chip 
              label={`Proje #${projectId}`} 
              size="small" 
              sx={styles.projectChip}
            />
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        {loading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={40} thickness={4} />
            <Typography sx={{ color: '#64748b' }}>
              Yorumlar yükleniyor...
            </Typography>
          </Box>
        ) : comments.length === 0 ? (
          <Box sx={styles.emptyState}>
            <ChatBubbleOutlineIcon sx={styles.emptyIcon} />
            <Typography variant="h6" sx={{ color: '#64748b', marginBottom: '8px' }}>
              Henüz yorum yok
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              Bu proje için ilk yorumu siz ekleyin
            </Typography>
          </Box>
        ) : (
          <Box>
            {comments.map((comment, index) => (
              <Fade in key={index} timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                <Paper sx={styles.commentCard} elevation={0}>
                  <Box sx={styles.commentHeader}>
                    <Avatar sx={styles.avatar}>
                      {getInitials(comment.kaydeden)}
                    </Avatar>
                    <Box sx={styles.commentMeta}>
                      <Typography sx={styles.userName}>
                        {comment.kaydeden}
                      </Typography>
                      <Box sx={styles.dateTime}>
                        <AccessTimeIcon sx={{ fontSize: '14px' }} />
                        {formatDate(comment.YorumTarihi)}
                      </Box>
                    </Box>
                  </Box>
                  <Typography sx={styles.commentText}>
                    {comment.Yorum}
                  </Typography>
                </Paper>
              </Fade>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <TextField
          label="Yorumunuzu yazın"
          placeholder="Bu proje hakkında düşüncelerinizi paylaşın..."
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          sx={styles.textField}
          disabled={sending}
        />
        <Box sx={styles.buttonContainer}>
          <Button 
            variant="contained"
            color="secondary"
            endIcon={<CloseIcon />}
            onClick={handleClose}
            sx={styles.closeActionButton}
          >
            Kapat
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSend} 
            disabled={!newComment.trim() || sending}
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={styles.sendButton}
          >
            {sending ? 'Gönderiliyor...' : 'Gönder'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CommentPopup;
